import * as yup from "yup";
import React, { useEffect, useState } from "react";

import { Grid, MenuItem, TextField, Typography } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";

import useAxios from "../../../../hooks/useAxios";
import useAlert from "../../../../hooks/useAlert";
import {
  __createTmobilejMessageFilter,
  __deleteTmobilejMessageFilter,
  __getTmobilejCuratedGroups,
  __updateTmobilejMessageFilter,
} from "../../../../api/sim";
import {
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  OutlinedInput,
  Select,
} from "@mui/material";

const lineValidationSchema = yup.object({
  number: yup
    .string("number")
    .min(3, "Should be between 3 and 10 digits long")
    .max(10, "Should be between 3 and 10 digits long")
    .required(),
});

const validateNumbersText = (data) => {
  const errors = {};
  if (!data) return { errors, numbers: [] };

  const numbers = [];
  const lines = data.split("\n");
  lines.some((line, idx) => {
    const trimmed = String(line || "").trim();
    if (!trimmed) return false; // allow empty lines

    const fields = trimmed.split(",");
    if (fields.length < 1) {
      errors.lines = `Invalid format in line ${idx + 1}: ${line}`;
      return true;
    }

    try {
      const obtainedData = { number: fields[0].trim() };
      lineValidationSchema.validateSync(obtainedData);
      if (isNaN(obtainedData.number)) throw new Error("Invalid Number");

      numbers.push(obtainedData.number);
      if (numbers.indexOf(obtainedData.number) !== numbers.length - 1) {
        errors.lines = `Duplicate number value in line ${idx + 1}`;
        return true;
      }
    } catch (err) {
      errors.lines = `Invalid format in line ${idx + 1}: ${line}`;
      return true;
    }

    return false;
  });

  return { errors, numbers };
};

const fromE164To10Digits = (value) => {
  const s = String(value || "").trim();
  if (/^\+1\d+$/.test(s)) return s.slice(2);
  if (/^\d{11}$/.test(s) && s.startsWith("1")) return s.slice(1);
  if (/^\d{10}$/.test(s)) return s;
  return "";
};

const stringify10DigitList = (nums) => {
  if (!Array.isArray(nums) || nums.length === 0) return "";
  return nums
    .map((n) => {
      const str = String(n || "").trim();
      // Accept E.164 with +1 or 11 digit 1xxxxxxxxxx or 10 digits, or 3-9 digits (short code)
      if (/^\+1\d{10}$/.test(str)) return str.slice(2);
      if (/^1\d{10}$/.test(str)) return str.slice(1);
      if (/^\d{10}$/.test(str)) return str;
      if (/^\d{3,9}$/.test(str)) return str; // Allow short code (3-9 digits)
      return "";
    })
    .filter(Boolean)
    .join("\n");
};

const pickMessageFilterId = (filter) =>
  filter?.FilterId ||
  filter?.MessageFilterId ||
  filter?.filterId ||
  filter?.id ||
  null;

const pickMessageFilterMode = (filter) =>
  filter?.FilterMode || filter?.filterMode || filter?.mode || "WHITELIST";

const pickAllowedContacts = (filter) =>
  filter?.PersonalNumbers ||
  filter?.AllowedContacts ||
  filter?.allowedContacts ||
  filter?.AllowedContact ||
  [];

const pickBlockedContacts = (filter) =>
  filter?.PersonalNumbers ||
  filter?.BlockedContacts ||
  filter?.blockedContacts ||
  filter?.BlockedContact ||
  [];

const MessageFilters = ({
  phone,
  subscriberId: propSubscriberId,
  phoneError,
  validatePhone,
  initialData,
  onDataUpdate,
}) => {
  const { setAlertMessage } = useAlert();

  const [messageFilterId, setMessageFilterId] = useState(null);
  const [mode, setMode] = useState("WHITELIST");
  const [allowedContactsText, setAllowedContactsText] = useState("");
  const [blockedContactsText, setBlockedContactsText] = useState("");
  const [allowedContacts, setAllowedContacts] = useState([]);
  const [blockedContacts, setBlockedContacts] = useState([]);
  const [allowedContactsError, setAllowedContactsError] = useState("");
  const [blockedContactsError, setBlockedContactsError] = useState("");
  const [applyToOutbound, setApplyToOutbound] = useState(null);
  const [applyToInbound, setApplyToInbound] = useState(null);
  const [blockUnknownNumbers, setBlockUnknownNumbers] = useState(null);
  const [blockLinks, setBlockLinks] = useState(null);
  const [blockMedia, setBlockMedia] = useState(null);

  const [availableGroups, setAvailableGroups] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);

  const [groupsResp, fetchGroups] = useAxios();

  useEffect(() => {
    fetchGroups(__getTmobilejCuratedGroups());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (groupsResp?.data) {
      setAvailableGroups(groupsResp.data);
    }
  }, [groupsResp]);

  const validateMessageLists = () => {
    if (mode === "WHITELIST") {
      const { errors, numbers } = validateNumbersText(allowedContactsText);
      setAllowedContactsError(errors.lines || "");
      setAllowedContacts(errors.lines ? [] : numbers);
      return !errors.lines;
    }
    const { errors, numbers } = validateNumbersText(blockedContactsText);
    setBlockedContactsError(errors.lines || "");
    setBlockedContacts(errors.lines ? [] : numbers);
    return !errors.lines;
  };

  const [createMsgResp, createMsgCall, createMsgLoading] = useAxios();
  const [updateMsgResp, updateMsgCall, updateMsgLoading] = useAxios();
  const [deleteMsgResp, deleteMsgCall, deleteMsgLoading] = useAxios();

  const hydrateMessageFromData = (data) => {
    // Backend returns the filter object directly or wrapped.
    const rawFilter = data?.filter || data || null;

    // Handle both array and object responses
    let f = null;
    if (Array.isArray(rawFilter) && rawFilter.length > 0) {
      f = rawFilter[0];
    } else if (rawFilter && typeof rawFilter === "object") {
      f = rawFilter;
    }

    if (!f || Object.keys(f).length === 0) {
      // No filter found, reset to empty state
      setMessageFilterId(null);
      setMode("WHITELIST");
      setAllowedContactsText("");
      setBlockedContactsText("");
      setAllowedContacts([]);
      setBlockedContacts([]);
      setAllowedContactsError("");
      setBlockedContactsError("");
      return;
    }

    setMessageFilterId(pickMessageFilterId(f));
    setMode(pickMessageFilterMode(f));

    const allowedContactsList = pickAllowedContacts(f);
    const blockedContactsList = pickBlockedContacts(f);
    const allowedStr = stringify10DigitList(allowedContactsList);
    const blockedStr = stringify10DigitList(blockedContactsList);

    setAllowedContactsText(allowedStr);
    setBlockedContactsText(blockedStr);

    const allowedParsed = validateNumbersText(allowedStr);
    setAllowedContactsError(allowedParsed.errors.lines || "");
    setAllowedContacts(allowedParsed.errors.lines ? [] : allowedParsed.numbers);

    const blockedParsed = validateNumbersText(blockedStr);
    setBlockedContactsError(blockedParsed.errors.lines || "");
    setBlockedContacts(blockedParsed.errors.lines ? [] : blockedParsed.numbers);

    // Extract view-only fields
    setApplyToOutbound(f?.ApplyToOutbound ?? null);
    setApplyToInbound(f?.ApplyToInbound ?? null);
    setBlockUnknownNumbers(f?.BlockUnknownNumbers ?? null);
    setBlockLinks(f?.BlockLinks ?? null);
    setBlockMedia(f?.BlockMedia ?? null);

    const selGroups = f?.SelectedGroups || [];
    setSelectedGroupIds(selGroups.map((g) => g.id));
  };

  // Hydrate form when initialData changes
  useEffect(() => {
    hydrateMessageFromData(initialData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  useEffect(() => {
    if (!createMsgResp?.status) return;
    setAlertMessage({ type: "success", message: "Message filter created" });
    if (createMsgResp.data && onDataUpdate) {
      onDataUpdate(createMsgResp.data);
    }
    hydrateMessageFromData(createMsgResp.data);
  }, [createMsgResp]);

  useEffect(() => {
    if (!updateMsgResp?.status) return;
    setAlertMessage({ type: "success", message: "Message filter updated" });
    if (updateMsgResp.data && onDataUpdate) {
      onDataUpdate(updateMsgResp.data);
    }
    hydrateMessageFromData(updateMsgResp.data);
  }, [updateMsgResp]);

  useEffect(() => {
    if (!deleteMsgResp?.status) return;
    setAlertMessage({ type: "success", message: "Message filter deleted" });
    setMessageFilterId(null);
    setMode("WHITELIST");
    setAllowedContactsText("");
    setBlockedContactsText("");
    setAllowedContacts([]);
    setBlockedContacts([]);
    setAllowedContactsError("");
    setBlockedContactsError("");
    if (onDataUpdate) {
      onDataUpdate(null);
    }
  }, [deleteMsgResp]);

  const buildMessagePayload = () => {
    const payload = {
      Phone: phone,
      FilterMode: mode,
      ApplyToOutbound: true,
      ApplyToInbound: true,
      BlockUnknownNumbers: true,
      BlockLinks: true,
      BlockMedia: true,
      SelectedGroupIds: selectedGroupIds,
    };
    if (mode === "WHITELIST") {
      payload.AllowedContacts = allowedContacts;
      payload.BlockedContacts = [];
    } else {
      payload.AllowedContacts = [];
      payload.BlockedContacts = blockedContacts;
    }
    return payload;
  };

  const handleCreateMessage = async () => {
    try {
      const err = validatePhone(phone);
      if (err) throw new Error(err);
      if (!validateMessageLists()) throw new Error("Fix invalid numbers");
      await createMsgCall({
        ...__createTmobilejMessageFilter(propSubscriberId),
        data: buildMessagePayload(),
      });
    } catch (e) {
      setAlertMessage({ type: "error", message: e.message || "Invalid input" });
    }
  };

  const handleUpdateMessage = async () => {
    if (!messageFilterId) return;
    try {
      const err = validatePhone(phone);
      if (err) throw new Error(err);
      if (!validateMessageLists()) throw new Error("Fix invalid numbers");
      await updateMsgCall({
        ...__updateTmobilejMessageFilter(propSubscriberId, messageFilterId),
        data: buildMessagePayload(),
      });
    } catch (e) {
      setAlertMessage({ type: "error", message: e.message || "Invalid input" });
    }
  };

  const handleDeleteMessage = async () => {
    if (!messageFilterId) return;
    await deleteMsgCall(
      __deleteTmobilejMessageFilter(propSubscriberId, messageFilterId),
    );
  };

  const loading = createMsgLoading || updateMsgLoading || deleteMsgLoading;
  const hasMessageFilter = Boolean(messageFilterId);
  const hasMessageErrors =
    Boolean(phoneError) ||
    (mode === "WHITELIST"
      ? Boolean(allowedContactsError)
      : Boolean(blockedContactsError));

  return (
    <>
      <Grid item sx={{ mt: 2 }}>
        <Typography variant="body2">
          SubscriberId: {propSubscriberId || "—"} | FilterId:{" "}
          {messageFilterId || "—"}
        </Typography>
      </Grid>

      {!hasMessageFilter && (
        <Grid item>
          <Typography variant="subtitle2">
            No message filters available.
          </Typography>
        </Grid>
      )}

      {hasMessageFilter && (
        <Grid item>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Filter Settings (View Only):
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            ApplyToOutbound: {String(applyToOutbound ?? "—")} | ApplyToInbound:{" "}
            {String(applyToInbound ?? "—")}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            BlockUnknownNumbers: {String(blockUnknownNumbers ?? "—")} |
            BlockLinks: {String(blockLinks ?? "—")}
          </Typography>
          <Typography variant="body2">
            BlockMedia: {String(blockMedia ?? "—")}
          </Typography>
        </Grid>
      )}

      <>
        <Grid item sx={{ mt: 1 }}>
          <TextField
            select
            fullWidth
            label="Filter Mode"
            value={mode}
            onChange={(e) => {
              const next = e.target.value;
              setMode(next);
              if (next === "WHITELIST") {
                const parsed = validateNumbersText(allowedContactsText);
                setAllowedContactsError(parsed.errors.lines || "");
                setAllowedContacts(parsed.errors.lines ? [] : parsed.numbers);
              } else {
                const parsed = validateNumbersText(blockedContactsText);
                setBlockedContactsError(parsed.errors.lines || "");
                setBlockedContacts(parsed.errors.lines ? [] : parsed.numbers);
              }
            }}
          >
            <MenuItem value="WHITELIST">WHITELIST</MenuItem>
            <MenuItem value="BLACKLIST">BLACKLIST</MenuItem>
          </TextField>
        </Grid>

        <Grid item sx={{ mt: 1 }}>
          <FormControl fullWidth>
            <InputLabel id="curated-groups-label">Curated Groups</InputLabel>
            <Select
              labelId="curated-groups-label"
              multiple
              value={selectedGroupIds}
              onChange={(e) => setSelectedGroupIds(e.target.value)}
              input={<OutlinedInput label="Curated Groups" />}
              renderValue={(selected) => {
                return availableGroups
                  .filter((g) => selected.includes(g.id))
                  .map((g) => g.name)
                  .join(", ");
              }}
            >
              {availableGroups.map((group) => (
                <MenuItem key={group.id} value={group.id}>
                  <Checkbox checked={selectedGroupIds.indexOf(group.id) > -1} />
                  <ListItemText primary={group.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {mode === "WHITELIST" ? (
          <Grid item>
            <TextField
              fullWidth
              multiline
              minRows={4}
              label="Allowed Contacts (10 digits or short codes, one per line)"
              value={allowedContactsText}
              error={Boolean(allowedContactsError)}
              helperText={
                allowedContactsError ||
                "Digits only; 10-digit numbers or short codes (3-9 digits); one per line"
              }
              onChange={(e) => {
                const text = e.target.value;
                setAllowedContactsText(text);
                const parsed = validateNumbersText(text);
                setAllowedContactsError(parsed.errors.lines || "");
                setAllowedContacts(parsed.errors.lines ? [] : parsed.numbers);
              }}
            />
          </Grid>
        ) : (
          <Grid item>
            <TextField
              fullWidth
              multiline
              minRows={4}
              label="Blocked Contacts (10 digits or short codes, one per line)"
              value={blockedContactsText}
              error={Boolean(blockedContactsError)}
              helperText={
                blockedContactsError ||
                "Digits only; 10-digit numbers or short codes (3-9 digits); one per line"
              }
              onChange={(e) => {
                const text = e.target.value;
                setBlockedContactsText(text);
                const parsed = validateNumbersText(text);
                setBlockedContactsError(parsed.errors.lines || "");
                setBlockedContacts(parsed.errors.lines ? [] : parsed.numbers);
              }}
            />
          </Grid>
        )}

        <Grid item sx={{ display: "flex", gap: 1 }}>
          {!hasMessageFilter ? (
            <LoadingButton
              loading={createMsgLoading}
              disabled={loading || hasMessageErrors}
              variant="contained"
              onClick={handleCreateMessage}
            >
              Create Message Filter
            </LoadingButton>
          ) : (
            <>
              <LoadingButton
                loading={updateMsgLoading}
                disabled={loading || hasMessageErrors}
                variant="contained"
                onClick={handleUpdateMessage}
              >
                Update Message Filter
              </LoadingButton>
              <LoadingButton
                loading={deleteMsgLoading}
                disabled={loading}
                color="error"
                variant="outlined"
                onClick={handleDeleteMessage}
              >
                Delete Message Filter
              </LoadingButton>
            </>
          )}
        </Grid>
      </>
    </>
  );
};

export default MessageFilters;
